import { differenceInCalendarDays } from 'date-fns/differenceInCalendarDays';

import type { GhostChatSidebarItem } from '@/features/chat/ghost-chats';
import type { ProjectSidebarListItem } from '@/features/projects/projects';

import type { Project, TeamChatPoster, Thread } from '@littlebird/sdk';

/** A teammate's chat in a shared project, which the member's own chat list does not hold */
export type TeamChatRow = {
  readonly id: string;
  readonly title: string;
  readonly posters: readonly TeamChatPoster[];
  readonly unread: boolean;
};

export type CollapsibleSidebarSection = 'projects' | 'favorites' | 'recents';
export const INITIAL_PROJECT_CHAT_COUNT = 3;
export const RECENT_CHAT_LIMIT = 20;

type ProjectRowSection = Exclude<CollapsibleSidebarSection, 'recents'>;

type ChatItem = {
  id: string;
} & (
  | {
      type: 'header';
      section: Exclude<CollapsibleSidebarSection, 'projects'>;
    }
  | {
      type: 'favorite-project';
      section: 'favorites';
    }
  | {
      type: 'favorite-routine';
      section: 'favorites';
      routineId: number;
    }
  | {
      type: 'projects-in-favorites';
      section: 'projects';
    }
  | {
      type: 'empty-project';
      section: ProjectRowSection;
      projectId: string;
      title: string;
    }
  | {
      type: 'team-chat';
      section: ProjectRowSection;
      projectId: string;
      chat: TeamChatRow;
    }
  | {
      type: 'show-more-project-chats';
      section: ProjectRowSection;
      projectId: string;
    }
  | {
      type: 'show-more-chats';
      section: 'recents';
    }
  | {
      type: 'recency-label';
      section: 'recents';
      label: string;
    }
  | {
      type: 'thread';
      section: ProjectRowSection;
      projectId: string;
    }
  | {
      type: 'thread';
      section: Exclude<CollapsibleSidebarSection, 'projects'>;
    }
);

export type SidebarListItem =
  | ChatItem
  | GhostChatSidebarItem
  | ProjectSidebarListItem;

export const threadItemKey = (item: Extract<ChatItem, { type: 'thread' }>) =>
  'projectId' in item
    ? `${item.section}-${item.projectId}-${item.id}`
    : `${item.section}-${item.id}`;

export function buildProjectSidebarSections({
  favoriteProjects,
  projects,
  favoriteRoutineIds,
  pinnedChats,
  recentChats,
  ghostItems,
  expandedProjectIds,
  hasMoreChats,
  groupByRecency = false,
  collapsedGroups = new Set<string>(),
  now = new Date(),
  teamChats = new Map(),
}: {
  favoriteProjects: Project[];
  projects: Project[];
  favoriteRoutineIds: number[];
  pinnedChats: Thread[];
  recentChats: Thread[];
  ghostItems: GhostChatSidebarItem[];
  expandedProjectIds: ReadonlySet<string>;
  hasMoreChats: boolean;
  groupByRecency?: boolean;
  collapsedGroups?: ReadonlySet<string>;
  now?: Date;
  teamChats?: ReadonlyMap<string, readonly TeamChatRow[]>;
}): Record<CollapsibleSidebarSection, SidebarListItem[]> {
  const projectIds = new Set(
    [...favoriteProjects, ...projects].map((project) => project.id)
  );
  const projectChats = new Map<string, Thread[]>();

  for (const chat of [...pinnedChats, ...recentChats]) {
    if (!chat.projectId || !projectIds.has(chat.projectId)) continue;
    const groupedChats = projectChats.get(chat.projectId) ?? [];
    groupedChats.push(chat);
    projectChats.set(chat.projectId, groupedChats);
  }
  const projectChildItems = (
    projectId: string,
    section: ProjectRowSection
  ): ChatItem[] => {
    if (!expandedProjectIds.has(projectId)) return [];
    const chats = projectChats.get(projectId) ?? [];
    const teammateChats = teamChats.get(projectId) ?? [];
    const items: ChatItem[] = [];
    if (chats.length === 0 && teammateChats.length === 0) {
      items.push({
        id: `empty-project-${projectId}`,
        title: 'Drag a chat here to add it',
        type: 'empty-project',
        section,
        projectId,
      });
    }
    for (const chat of chats.slice(0, INITIAL_PROJECT_CHAT_COUNT)) {
      items.push({ id: chat.id, type: 'thread', section, projectId });
    }
    for (const chat of teammateChats) {
      items.push({
        id: `team-chat-${projectId}-${chat.id}`,
        type: 'team-chat',
        section,
        projectId,
        chat,
      });
    }
    if (chats.length > INITIAL_PROJECT_CHAT_COUNT) {
      items.push({
        id: `show-more-project-chats-${projectId}`,
        type: 'show-more-project-chats',
        section,
        projectId,
      });
    }
    return items;
  };

  const projectItems: SidebarListItem[] = [
    { id: 'projects-header', type: 'projects-header' },
  ];
  if (projects.length === 0) {
    projectItems.push(
      favoriteProjects.length === 0
        ? { id: 'add-project', type: 'add-project' }
        : {
            id: 'projects-in-favorites',
            type: 'projects-in-favorites',
            section: 'projects',
          }
    );
  }
  for (const project of projects) {
    projectItems.push(
      { id: project.id, type: 'project' },
      ...projectChildItems(project.id, 'projects')
    );
  }

  const favoriteCount =
    pinnedChats.length + favoriteProjects.length + favoriteRoutineIds.length;
  const favoriteItems: SidebarListItem[] =
    favoriteCount === 0
      ? []
      : [
          {
            id: 'favorites',
            type: 'header',
            section: 'favorites',
          },
          ...pinnedChats.map((chat): ChatItem => ({
            id: chat.id,
            type: 'thread',
            section: 'favorites',
          })),
          ...favoriteProjects.flatMap((project): ChatItem[] => [
            { id: project.id, type: 'favorite-project', section: 'favorites' },
            ...projectChildItems(project.id, 'favorites'),
          ]),
          ...favoriteRoutineIds.map((routineId): ChatItem => ({
            id: `routine-${routineId}`,
            type: 'favorite-routine',
            section: 'favorites',
            routineId,
          })),
        ];

  const recentItems: SidebarListItem[] = [
    {
      id: 'recents',
      type: 'header',
      section: 'recents',
    },
    ...recentChats.slice(0, RECENT_CHAT_LIMIT).flatMap((chat, index, chats) => {
      const item: ChatItem = {
        id: chat.id,
        type: 'thread',
        section: 'recents',
      };
      if (!groupByRecency) return [item];
      const label = recencyLabel(chat.lastMessageAt, now);
      const startsGroup =
        index > 0 &&
        recencyLabel(chats[index - 1].lastMessageAt, now) !== label;
      const labelItem = {
        id: `recency-${label}`,
        type: 'recency-label',
        section: 'recents',
        label,
      } as ChatItem;
      return [
        ...(startsGroup ? [labelItem] : []),
        ...(collapsedGroups.has(label) ? [] : [item]),
      ];
    }),
    ...ghostItems,
  ];
  if (
    recentChats.length > RECENT_CHAT_LIMIT ||
    (recentChats.length === RECENT_CHAT_LIMIT && hasMoreChats)
  ) {
    recentItems.push({
      id: 'show-more-chats',
      type: 'show-more-chats',
      section: 'recents',
    });
  }

  return {
    projects: projectItems,
    favorites: favoriteItems,
    recents: recentItems,
  };
}

export const recencyLabel = (date: Date, now: Date) => {
  const days = differenceInCalendarDays(now, date);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days <= 7) return 'Last 7 days';
  if (days <= 30) return 'Last 30 days';
  return 'Older';
};
